import { useState } from 'react'
import { Globe, ArrowRight } from 'lucide-react'
import ChatChoices from './ChatChoices'
import { MAIN_BRANCH_TEST_OPTIONS, PR_TEST_OPTIONS } from '../../lib/githubTestingOptions'
import type { GithubTestingAnswers } from '../../lib/githubTesting'
import type { GithubMainBranchTestMode, GithubPrTestMode } from '../../data/projectTypes'
import type { ChatChoice } from '../../../netlify/functions/lib/project-chat-types'

// The two GitHub testing questions — how to test main-branch deployments, then how to handle pull
// requests — shared by the chat onboarding walkthrough (OnboardingChat) and the running-project connect
// card (ProjectGithubChatCard) so both ask exactly the same thing and build the same settings. This
// component owns the main-branch → PRs step order and the "detect" deployment-URL sub-prompt; it
// narrates each answer through onNarrate and returns the collected answers via onComplete. It's purely
// a question flow — persistence (createProject / updateProject) is the host's job.

export default function GithubTestingQuestions({
  repoLabel,
  focus = 'all',
  current,
  defaultDeploymentUrl = '',
  onNarrate,
  onComplete,
}: {
  // Repo shown in the main-branch prompt (e.g. "owner/repo").
  repoLabel: string
  // Which question(s) to ask. 'all' (default) walks main-branch → PRs, as when linking a repo. A single
  // section asks ONLY that question and preserves the other from `current` — so the chat can jump the
  // user straight to the part they asked to change instead of the whole flow.
  focus?: 'all' | 'main-branch' | 'prs'
  // The connection's existing answers. Required backing for a single-section `focus` (the skipped
  // question's value is carried through unchanged); also seeds the main-branch selection.
  current?: GithubTestingAnswers
  // Prefill for the "detect" deployment-URL input (the project's existing app URL, when known).
  defaultDeploymentUrl?: string
  // Called (user-voice) as each question is answered, so the host can narrate it into a transcript.
  onNarrate?: (text: string) => void
  // Called once the asked question(s) are answered, with the assembled answers.
  onComplete: (answers: GithubTestingAnswers) => void
}) {
  // Focusing on just PRs starts on that step; otherwise begin with the main-branch question.
  const [step, setStep] = useState<'main-branch' | 'prs'>(focus === 'prs' ? 'prs' : 'main-branch')
  const [detectMode, setDetectMode] = useState(false)
  const [detectUrl, setDetectUrl] = useState(defaultDeploymentUrl || current?.mainBranchDeploymentUrl || '')
  // Seed from the existing connection so a PR-only edit carries the current main-branch answer through.
  const [mainBranch, setMainBranch] = useState<{ mode: GithubMainBranchTestMode; url: string }>({
    mode: current?.mainBranchTesting ?? 'none',
    url: current?.mainBranchDeploymentUrl ?? '',
  })

  function answerMainBranch(mode: GithubMainBranchTestMode, url = '') {
    const label = MAIN_BRANCH_TEST_OPTIONS.find((o) => o.value === mode)?.label ?? mode
    onNarrate?.(mode === 'detect' ? `Test main-branch deployments at ${url.trim()}.` : `Main branch: ${label}.`)
    const answer = { mode, url: mode === 'detect' ? url.trim() : '' }
    setMainBranch(answer)
    setDetectMode(false)
    // Focused on the main branch only: finish here, keeping the existing PR answer untouched.
    if (focus === 'main-branch') {
      onComplete({ mainBranchTesting: answer.mode, mainBranchDeploymentUrl: answer.url, prTesting: current?.prTesting ?? 'none' })
      return
    }
    setStep('prs')
  }

  function answerPrs(mode: GithubPrTestMode) {
    const label = PR_TEST_OPTIONS.find((o) => o.value === mode)?.label ?? mode
    onNarrate?.(`Pull requests: ${label}.`)
    onComplete({ mainBranchTesting: mainBranch.mode, mainBranchDeploymentUrl: mainBranch.url, prTesting: mode })
  }

  const handleMainBranchChoice = (choice: ChatChoice) => {
    if (choice.id === 'detect') { setDetectMode(true); return }
    answerMainBranch(choice.id as GithubMainBranchTestMode)
  }

  if (step === 'main-branch') {
    if (detectMode) {
      return (
        <div className="gh-testing" data-testid="github-testing-main-branch">
          <p className="gh-testing__text">
            What URL is your main branch deployed to? Replay QA will run when a new deployment goes live there.
          </p>
          <div className="gh-testing__url-field">
            <Globe size={16} strokeWidth={1.75} aria-hidden />
            <input
              type="url"
              inputMode="url"
              className="gh-testing__url-input"
              value={detectUrl}
              onChange={(e) => setDetectUrl(e.target.value)}
              placeholder="https://your-app.com"
              aria-label="Main branch deployment URL"
              autoFocus
              data-testid="github-testing-deploy-url"
            />
          </div>
          <div className="gh-testing__actions">
            <button
              type="button"
              className="gh-testing__btn"
              onClick={() => answerMainBranch('detect', detectUrl)}
              disabled={!detectUrl.trim()}
              data-testid="github-testing-deploy-url-confirm"
              data-analytics-event="github-testing-main-branch-detect"
            >
              Continue <ArrowRight size={14} aria-hidden />
            </button>
            <button type="button" className="gh-testing__back" onClick={() => setDetectMode(false)}>Back</button>
          </div>
        </div>
      )
    }
    return (
      <div className="gh-testing" data-testid="github-testing-main-branch">
        <p className="gh-testing__text">
          Whenever your main branch is deployed, Replay QA can automatically test the live site so
          regressions are caught before your users hit them. Choose how QA should pick up new main-branch
          deployments — each option below explains what it does.
        </p>
        <ChatChoices
          choiceSet={{
            prompt: `How should Replay QA test the main branch of ${repoLabel}?`,
            choices: MAIN_BRANCH_TEST_OPTIONS.filter((o) => !o.comingSoon).map((o) => ({ id: o.value, label: o.label, description: o.description, action: { kind: 'message', message: o.value } })),
          }}
          onSelect={handleMainBranchChoice}
        />
      </div>
    )
  }

  return (
    <div className="gh-testing" data-testid="github-testing-prs">
      <p className="gh-testing__text">
        Replay QA can also test the preview deployment of every pull request and post the results back as
        a comment, so problems surface during review instead of after merge. Choose how QA should handle
        pull requests — each option below explains what it does.
      </p>
      <ChatChoices
        choiceSet={{
          prompt: 'How should Replay QA handle pull requests?',
          choices: PR_TEST_OPTIONS.filter((o) => !o.comingSoon).map((o) => ({ id: o.value, label: o.label, description: o.description, action: { kind: 'message', message: o.value } })),
        }}
        onSelect={(c) => answerPrs(c.id as GithubPrTestMode)}
      />
    </div>
  )
}
